export type ProductUploadImageSize = "thumbs" | "small" | "medium" | "large";

const PRODUCT_UPLOAD_PATH = "/upload/product/product";

const DERIVATIVE_SEGMENTS: ReadonlySet<string> = new Set([
    "thumbs",
    "small",
    "medium",
    "large"
]);

/**
 * Nominal widths for `w` descriptors on listing cards; align with real derivative sizes when known.
 */
export const PRODUCT_CARD_IMAGE_SRC_WIDTHS = {
    small: 400,
    medium: 800
} as const;

/** Default `sizes` for product-card grid (also used by `productUploadImageCardResponsive`). */
export const PRODUCT_IMAGE_SIZES_CARD =
    "(max-width: 767px) 50vw, (max-width: 1279px) 33vw, 280px";

/** Cart / checkout line thumbnails. */
export const PRODUCT_IMAGE_SIZES_CART_THUMB =
    "(max-width: 767px) 25vw, 120px";

/** User list rows (ImageUpdateListItem, NewListItem, ListPage table image). */
export const PRODUCT_IMAGE_SIZES_LIST_ROW =
    "(max-width: 767px) 35vw, 220px";

/** Return flow tables. */
export const PRODUCT_IMAGE_SIZES_RETURN_TABLE =
    "(max-width: 767px) 28vw, 100px";

/** Recent-order / dashboard product stacks (match .order-products .image-container in account-recent-order.scss). */
export const PRODUCT_IMAGE_SIZES_ORDER_TILE =
    "(max-width: 989px) 58px, (max-width: 1279px) 70px, 100px";

/** Favorites account page tile (`.favorite-details .image-container` — 6.25rem). */
export const PRODUCT_IMAGE_SIZES_FAVORITES =
    "(max-width: 767px) 30vw, 100px";

/** Quick ordering / express product grid cells. */
export const PRODUCT_IMAGE_SIZES_EXPRESS_GRID =
    "(max-width: 767px) 45vw, 360px";

/**
 * Builds an absolute URL for a product image stored under the legacy upload path.
 * @param filename Stored image file name (e.g. from primary_image / image_filename)
 * @param size Optional derivative folder; omit or null for full-size (no subfolder)
 */
export function productUploadImageUrl(
    filename: string,
    size?: ProductUploadImageSize | null
): string {
    const base = (import.meta.env.VITE_APP_URL ?? "").replace(/\/$/, "");
    const segment = size ? `${size}/` : "";
    return `${base}${PRODUCT_UPLOAD_PATH}/${segment}${filename}`;
}

function productUploadImageSrcSetSmallMedium(filename: string): string {
    const { small: wSmall, medium: wMedium } = PRODUCT_CARD_IMAGE_SRC_WIDTHS;
    return `${productUploadImageUrl(filename, "small")} ${wSmall}w, ${productUploadImageUrl(filename, "medium")} ${wMedium}w`;
}


export function resolveProductUploadDisplayUrl(filename: string, size): string {
   // if filename is a full URL, take the last part of the URL as a filename and pass it to productUploadImageUrl
   if (filename.startsWith("http")) {
    return productUploadImageUrl(filename.split("/").pop() ?? "", size);
   }
    return productUploadImageUrl(filename, size);
}

/**
 * Returns the stored filename for a full URL or path under `/upload/product/product/…`,
 * stripping an optional derivative folder (small, medium, etc.). Returns null if not a product upload URL.
 */
export function parseProductUploadImageFilename(imageUrl: string): string | null {
    const base = (import.meta.env.VITE_APP_URL ?? "http://localhost").replace(
        /\/$/,
        ""
    );
    let pathname: string;
    try {
        if (/^https?:\/\//i.test(imageUrl)) {
            pathname = new URL(imageUrl).pathname;
        } else {
            const path = imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;
            pathname = new URL(path, `${base}/`).pathname;
        }
    } catch {
        return null;
    }

    const marker = `${PRODUCT_UPLOAD_PATH}/`;
    const idx = pathname.indexOf(marker);
    if (idx === -1) {
        return null;
    }

    const rest = pathname.slice(idx + marker.length);
    const segments = rest.split("/").filter(Boolean);
    if (segments.length === 0) {
        return null;
    }

    if (
        segments.length >= 2 &&
        DERIVATIVE_SEGMENTS.has(segments[0] as string)
    ) {
        return segments.slice(1).join("/") || null;
    }

    return segments.join("/") || null;
}

/**
 * Full-size `src` plus small/medium `srcset` for a stored filename.
 */
export function productUploadImageResponsiveFromFilename(
    filename: string,
    sizes: string
): { src: string; srcSet: string; sizes: string } {
    return {
        src: productUploadImageUrl(filename, null),
        srcSet: productUploadImageSrcSetSmallMedium(filename),
        sizes
    };
}


/**
 * Full-size URL as `src` fallback, plus small/medium `srcset` for product cards.
 */
export function productUploadImageCardResponsive(imageUrl: string): {
    mediaSrc: string;
    srcSet: string;
    sizes: string;
} | null {
    const filename = parseProductUploadImageFilename(imageUrl);
    if (!filename) {
        return null;
    }
    const pack = productUploadImageResponsiveFromFilename(
        filename,
        PRODUCT_IMAGE_SIZES_CARD
    );
    return {
        mediaSrc: pack.src,
        srcSet: pack.srcSet,
        sizes: pack.sizes
    };
}
